import { ORPCError } from "@orpc/client";
import {
  Button,
  buttonVariants,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  focusHaloFlush,
  Input,
  Label,
  Skeleton,
} from "@pax/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import { type SubmitEvent, useEffect, useState } from "react";
import { api, orpc, reachable, requestErrorMessage } from "@/lib/api";
import {
  ACCOUNT_DELETED_PATH,
  ACCOUNT_DELETION_PATH,
  canAuthorizeDeletion,
  destructiveSolid,
  ErrorMessage,
  formatMoment,
  RESUME_PARAM,
  SignInAgain,
  useAccountDeletionStatus,
  withoutResume,
} from "../account-deletion";
import { type Session, sessionKey, signOut, useSession } from "../queries";

/** What the owner types to delete their account at once. */
const CONFIRMATION = "DELETE";

/**
 * The whole window while the signed-in account waits to be deleted: no sidebar, panels, or footer,
 * and every address leads here. Cancelling or signing out brings the rest of Pax back.
 */
export function AccountDeletionLock() {
  const pathname = useLocation({ select: location => location.pathname });
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (pathname !== ACCOUNT_DELETION_PATH) {
      void navigate({ to: ACCOUNT_DELETION_PATH, replace: true });
    }
  }, [navigate, pathname]);

  async function handleSignOut() {
    try {
      await signOut(queryClient);
    } finally {
      await navigate({ to: "/" });
      await router.invalidate();
    }
  }

  return (
    <div className="fixed inset-0 flex flex-col overflow-y-auto bg-background">
      <AccountDeletionPage />
      <button
        type="button"
        onClick={() => void handleSignOut()}
        className={cn(
          "mx-auto mb-6 rounded text-xs text-muted-foreground hover:text-foreground",
          focusHaloFlush,
        )}
      >
        Sign out
      </button>
    </div>
  );
}

/**
 * A pending deletion, counting down to its purge, with a way to keep the account and a quieter way
 * to delete it now.
 */
export function AccountDeletionPage() {
  const { data: session } = useSession();
  const now = useNow();
  const scheduled = session?.user.deletionPurgeAfter;
  const due = Boolean(scheduled && now >= new Date(scheduled).getTime());
  const deletion = useAccountDeletionStatus(session, { watch: due });
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const keep = useMutation({
    mutationFn: () => reachable(api.accounts.deletion.delete()),
    onSuccess: async () => {
      queryClient.setQueryData(orpc.accounts.deletion.get.queryKey(), { deletion: null });
      await queryClient.invalidateQueries({ queryKey: sessionKey });
      await navigate({ to: "/" });
    },
  });

  if (!session) {
    return (
      <Centered>
        <Skeleton className="h-40 w-full rounded-2xl" />
      </Centered>
    );
  }

  // The session says whether a deletion is pending and when; the deletion itself only adds
  // whether its purge has started.
  if (!scheduled) {
    return (
      <Centered>
        <h1 className="text-2xl font-semibold tracking-tight">Your account isn't being deleted</h1>
        <Link to="/" className={buttonVariants({ size: "lg", className: "mt-8 w-full" })}>
          Back to Pax
        </Link>
      </Centered>
    );
  }

  const purgeAfter = new Date(scheduled).toISOString();
  const started = Boolean(deletion.data?.purgeStartedAt);
  // Once the time comes, the purge starts within moments; until it does, cancelling still works.
  if (started || due) {
    return (
      <Centered>
        <h1 className="text-2xl font-semibold tracking-tight">Your account is being deleted</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {started ? "Deletion has started, so it can't be cancelled." : "This takes a moment."}
        </p>
        <ErrorMessage
          className="mt-6 w-full"
          message={keep.error ? requestErrorMessage(keep.error) : null}
        />
        {started ? null : (
          <Button
            variant="ghost"
            className="mt-6"
            disabled={keep.isPending}
            onClick={() => keep.mutate()}
          >
            Cancel deletion
          </Button>
        )}
      </Centered>
    );
  }

  return (
    <Centered>
      <p className="text-sm text-muted-foreground">Your account will be deleted in</p>
      <Countdown until={purgeAfter} now={now} />
      <p className="mt-4 text-sm text-muted-foreground">{formatMoment(purgeAfter)}</p>

      <ErrorMessage
        className="mt-6 w-full"
        message={keep.error ? requestErrorMessage(keep.error) : null}
      />
      <Button
        size="lg"
        className="mt-8 w-full"
        disabled={keep.isPending}
        onClick={() => keep.mutate()}
      >
        Cancel deletion
      </Button>
      {session.user.isAnonymous && (
        <p className="mt-4 text-sm text-pretty text-muted-foreground">
          Keep this browser session to cancel. If you sign out or clear your browser data, you can’t
          return to this guest account.
        </p>
      )}
      <DeleteNow session={session} purgeAfter={purgeAfter} />
    </Centered>
  );
}

/**
 * The whole window once an account is deleted, saying when its owner asked and when it went. Deleting
 * now lands here, and so does signing in again with a deleted account's email.
 */
export function AccountDeletedPage({
  requested,
  deleted,
}: {
  requested?: string;
  deleted?: string;
}) {
  return (
    <div className="fixed inset-0 flex flex-col overflow-y-auto bg-background">
      <Centered>
        <h1 className="text-2xl font-semibold tracking-tight">Your account has been deleted</h1>
        {requested && deleted ? (
          <p className="mt-3 text-sm text-pretty text-muted-foreground">
            You chose to delete it on {formatMoment(requested)}, and it was deleted on{" "}
            {formatMoment(deleted)}.
          </p>
        ) : null}
        <Link to="/" className={buttonVariants({ size: "lg", className: "mt-8 w-full" })}>
          Back to Pax
        </Link>
      </Centered>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-full w-full flex-1 max-w-sm flex-col items-center justify-center px-6 py-16 text-center">
      {children}
    </main>
  );
}

const UNITS = [
  { label: "days", ms: 24 * 60 * 60 * 1000 },
  { label: "hours", ms: 60 * 60 * 1000 },
  { label: "minutes", ms: 60 * 1000 },
  { label: "seconds", ms: 1000 },
] as const;

/** The time now, ticking every second. */
function useNow(): number {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

/** Days, hours, minutes, and seconds until a moment. */
function Countdown({ until, now }: { until: string; now: number }) {
  let remaining = Math.max(0, new Date(until).getTime() - now);
  const parts = UNITS.map(({ label, ms }) => {
    const value = Math.floor(remaining / ms);
    remaining -= value * ms;
    return { label, value };
  });

  return (
    <div className="mt-3 grid w-full grid-cols-4 gap-2" role="timer" aria-live="off">
      {parts.map(({ label, value }) => (
        <div key={label} className="flex flex-col items-center rounded-xl bg-muted/50 py-3">
          <span className="text-3xl font-semibold tracking-tight tabular-nums">
            {String(value).padStart(2, "0")}
          </span>
          <span className="mt-1 text-[11px] text-muted-foreground">{label}</span>
        </div>
      ))}
    </div>
  );
}

/** The quieter way out: type to confirm, then the account is purged at once. */
function DeleteNow({ session, purgeAfter }: { session: Session; purgeAfter: string }) {
  const href = useLocation({ select: location => location.href });
  const navigate = useNavigate();
  const resuming = new URL(href, window.location.origin).searchParams.get(RESUME_PARAM) === "now";
  const [open, setOpen] = useState(resuming);

  useEffect(() => {
    if (resuming) void navigate({ href: withoutResume(href), replace: true });
  }, [href, navigate, resuming]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "mt-4 rounded text-xs text-muted-foreground underline-offset-4 hover:text-destructive hover:underline",
          focusHaloFlush,
        )}
      >
        Delete now instead
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md gap-0 p-6">
          {open ? <DeleteNowSteps session={session} purgeAfter={purgeAfter} /> : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

function DeleteNowSteps({ session, purgeAfter }: { session: Session; purgeAfter: string }) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const navigate = useNavigate();
  const [typed, setTyped] = useState("");
  const [signInAgain, setSignInAgain] = useState(false);

  const deleteNow = useMutation({
    mutationFn: () => reachable(api.accounts.deletion.create({ body: { when: "now" } })),
    onSuccess: async deletion => {
      // The API has ended every session; this clears what the browser still holds.
      await signOut(queryClient).catch(() => undefined);
      await navigate({
        to: ACCOUNT_DELETED_PATH,
        search: { requested: deletion.requestedAt, deleted: deletion.purgeAfter },
      });
      await router.invalidate();
    },
    onError: error => {
      if (error instanceof ORPCError && error.code === "SIGN_IN_AGAIN") setSignInAgain(true);
    },
  });

  if (signInAgain) return <SignInAgain resume="now" onBack={() => setSignInAgain(false)} />;

  function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    if (typed !== CONFIRMATION) return;
    if (canAuthorizeDeletion(session)) deleteNow.mutate();
    else setSignInAgain(true);
  }

  return (
    <form onSubmit={submit}>
      <DialogTitle className="text-lg">Delete your account now?</DialogTitle>
      <DialogDescription className="mt-1.5 text-pretty">
        This can't be undone. Your account is deleted right away, instead of on{" "}
        {formatMoment(purgeAfter)}.
      </DialogDescription>

      <Label htmlFor="confirm-account-deletion" className="mt-5 block text-sm font-normal">
        Type <span className="font-mono font-semibold">{CONFIRMATION}</span> to confirm
      </Label>
      <Input
        id="confirm-account-deletion"
        value={typed}
        onChange={event => setTyped(event.target.value)}
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        autoFocus
        className="mt-2 font-mono"
      />

      <ErrorMessage
        className="mt-4"
        message={deleteNow.error && !signInAgain ? requestErrorMessage(deleteNow.error) : null}
      />
      <Button
        type="submit"
        size="lg"
        className={cn("mt-6 w-full", destructiveSolid)}
        disabled={typed !== CONFIRMATION || deleteNow.isPending}
      >
        Delete my account
      </Button>
    </form>
  );
}
